// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { documentsApi } from '@/lib/api/documents'
import { queryKeys } from '@/constants/queryKeys'
import type { DocumentFilter } from '@/types'

export function useDocumentList(filter?: DocumentFilter) {
  return useQuery({
    queryKey: queryKeys.documents.list(filter),
    queryFn: () => documentsApi.list(filter),
  })
}

export function useDocument(id: string) {
  return useQuery({
    queryKey: queryKeys.documents.detail(id),
    queryFn: () => documentsApi.getById(id),
    enabled: !!id,
  })
}

export function useDocumentAudit(id: string) {
  return useQuery({
    queryKey: queryKeys.documents.audit(id),
    queryFn: () => documentsApi.getAuditLog(id),
    enabled: !!id,
  })
}

export function useUploadDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ file, onProgress }: { file: File; onProgress?: (pct: number) => void }) =>
      documentsApi.upload(file, onProgress),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents.all() })
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => documentsApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents.all() })
    },
  })
}

export function useVerifyDocument() {
  return useMutation({
    mutationFn: (id: string) => documentsApi.verify(id),
  })
}
